import { useAuth } from "@/context/auth-context";
import { GetReportResponse, useDatasetInfo } from "@/queries";

import { ColumnType, Report, ReportCell, ReportColumn } from "@/types/evaluate";
import { sortColumns } from "@/utils/evaluate";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import HeaderCell from "./TableComponents/Header";
import { DatasetRead } from "@/types/datasets";
import RowCell from "./TableComponents/RowCell/";
import { usePagination } from "@/hooks/UsePagination";
import { ENDPOINTS } from "@/api/application-api";
import { useQueryClient } from "@tanstack/react-query";
import { useGetDatasetGroups } from "@/queries/datasets";
import { DatasetGroup } from "@/queries/datasets/types";

export interface SortConfigItem {
  columnId: number;
  direction: "asc" | "desc" | null;
}

interface ReportContextType extends Record<string, any> {
  reportMetadata: GetReportResponse;
  filters: { [columnId: string]: string[] };
  setFilters: React.Dispatch<
    React.SetStateAction<{ [columnId: string]: string[] }>
  >;

  sortConfig: SortConfigItem[];
  globalSearchTerm: string | null;

  reportCells: ReportCell[] | null;
  setReportCells: React.Dispatch<React.SetStateAction<ReportCell[] | null>>;
  setGlobalSearchTerm: React.Dispatch<React.SetStateAction<string | null>>;
  visibleRows: number[] | null;
  setVisibleRows: (rows: number[]) => void;
}

export const ReportContext = createContext<ReportContextType | null>(null);

interface ReportProviderProps extends PropsWithChildren<{}> {
  reportMetadata: GetReportResponse;
  editable: boolean;
}

function ReportProvider({ reportMetadata, ...props }: ReportProviderProps) {
  const userToken = useAuth()?.userToken || "";
  const reportId = reportMetadata.report.id;
  const queryClient = useQueryClient();
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reportColumns, setReportColumns] = useState<ReportColumn[]>([]);
  const [reportCells, setReportCells] = useState<ReportCell[] | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | undefined>();
  const [visibleRows, setVisibleRows] = useState<number[] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortedRowIds, setSortedRowIds] = useState<number[] | null>(null);
  const [cellStatus, setCellStatus] = useState<string[] | null>(null);

  const setVisibleRowsAndClearUpdated = useCallback(
    (newVisibleRows: number[]) => {
      if (!visibleRows || visibleRows.join(",") !== newVisibleRows.join(",")) {
        setLastUpdated(undefined);
        setVisibleRows(newVisibleRows);
      }
    },
    [visibleRows],
  );

  const [lastCellNumber, setLastCellNumber] = useState<number | null>(null);
  useEffect(() => {
    if (reportMetadata) {
      let newCount = Object.values(reportMetadata.stats.status_counts).reduce(
        (total, value) => total + value,
        0,
      );
      if (lastCellNumber !== newCount) {
        setLastCellNumber(newCount);
        setLastUpdated(undefined);
        queryClient.invalidateQueries([ENDPOINTS.report_row_ids(reportId)]);
      }
    }
  }, [reportMetadata, lastCellNumber, queryClient, reportId]);

  const paginationRange = usePagination({
    totalCount: (sortedRowIds || []).length,
    pageSize,
  });

  useEffect(() => {
    if (!sortedRowIds) {
      return;
    }
    let newVisibleRows = sortedRowIds.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    );
    if (!visibleRows || visibleRows.join(",") !== newVisibleRows.join(",")) {
      setVisibleRows(newVisibleRows);
    }
  }, [
    visibleRows,
    setVisibleRows,
    sortedRowIds,
    currentPage,
    pageSize,
    paginationRange.length,
  ]);

  // Reset current page when page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  // getters

  const { data: datasetInfoResponse } = useDatasetInfo(
    userToken,
    reportMetadata.report.dataset_id,
  );

  const datasetInfo = {
    ...datasetInfoResponse,
  };

  const getCellByRowAndColumn = useCallback(
    (rowId: number, columnId: number) => {
      return reportCells?.find(
        (cell: ReportCell) =>
          cell.dataset_row_id === rowId && cell.report_column_id === columnId,
      );
    },
    [reportCells],
  );

  // These table helpers are calculated in the context so that
  // the table doesn't have to re-render when cells are updated

  // Building columns
  const sortedColumns = useMemo(
    () => sortColumns(reportColumns || []),
    [reportColumns],
  );
  const lastColumnId = useMemo(() => {
    const lastColumn = sortedColumns[sortedColumns.length - 1];
    return lastColumn?.id || -1;
  }, [sortedColumns]);

  const headerCellsList = useMemo(
    () =>
      sortedColumns.map((column: ReportColumn) => {
        return () =>
          HeaderCell({
            column,
            reportId,
            availableColumns: sortedColumns,
            isEditable:
              props.editable && column.column_type !== ColumnType.DATASET,
          });
      }),
    [sortedColumns, reportId, props.editable],
  );
  const tableColumnsMap = useMemo(() => {
    const columnCellsMap: Record<number, ReportColumn> = {};
    sortedColumns.forEach((column: ReportColumn) => {
      const columnId: number = column.id || -1;
      columnCellsMap[columnId] = column;
    });
    return columnCellsMap;
  }, [sortedColumns]);
  const getColumnById = useCallback(
    (columnId: number) => {
      return tableColumnsMap[columnId];
    },
    [tableColumnsMap],
  );

  // Building cells
  const tableCellsMap = useMemo(() => {
    if (sortedRowIds === null) {
      return {};
    }
    const rowColumnCellsMap: any = {};
    sortedRowIds.forEach((rowId: number) =>
      sortedColumns.forEach((column: ReportColumn) => {
        const mapId = `${rowId}-${column.id}`;
        const columnId: number = column.id || -1;
        rowColumnCellsMap[mapId] = () => (
          <RowCell rowId={rowId} columnId={columnId} />
        );
      }),
    );
    return rowColumnCellsMap;
  }, [sortedRowIds, sortedColumns]);

  // Filter/ Sort/ Search
  const [filters, setFilters] = useState<{
    [columnId: string]: string[];
  }>({});

  const [sortConfig, setSortConfig] = useState<SortConfigItem[]>([]);

  const [currentFilterInput, setCurrentFilterInput] = useState<{
    column_id: string;
    value: string;
  } | null>(null);

  const [globalSearchTerm, setGlobalSearchTerm] = useState<null | string>(null);

  const sortColumn = useCallback(
    (columnId: number, direction: "asc" | "desc" | null) => {
      setSortConfig((prevConfig) => {
        const columnIndex = prevConfig.findIndex(
          (item) => item.columnId === columnId,
        );

        if (columnIndex === -1) {
          return [...prevConfig, { columnId, direction }];
        } else {
          const newConfig = prevConfig.filter(
            (item) => item.columnId !== columnId,
          );
          if (direction !== null) newConfig.push({ columnId, direction });
          return newConfig;
        }
      });
    },
    [],
  );

  const { data: datasetGroups } = useGetDatasetGroups({
    dataSetId: reportMetadata.report.dataset_id.toString(),
    minVersionNumber: 0,
  });

  const currentDatasetGroup = datasetGroups?.dataset_groups?.find(
    (datasetGroup: DatasetGroup) =>
      datasetGroup.datasets?.find(
        (dataset: DatasetRead) =>
          dataset.id === reportMetadata.report.dataset_id,
      ),
  );

  const latestDataset =
    currentDatasetGroup?.datasets?.[currentDatasetGroup.datasets.length - 1];

  return (
    <ReportContext.Provider
      value={{
        columnsCount: sortedColumns.length,
        datasetInfo,
        latestDataset,
        getCellByRowAndColumn,
        getColumnById,
        headerCellsList,
        isEditable: props.editable,
        lastColumnId,
        reportCells,
        reportColumns,
        setReportColumns,
        setReportCells,
        filters,
        selectedReport,
        setSelectedReport,
        sortConfig,
        sortColumn,
        setFilters,
        setCurrentFilterInput,
        currentFilterInput,
        reportId,
        reportMetadata,
        sortedColumns,
        tableCellsMap,
        globalSearchTerm,
        setGlobalSearchTerm,
        lastUpdated,
        setLastUpdated,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        paginationRange,
        sortedRowIds,
        setSortedRowIds,
        visibleRows,
        setVisibleRows: setVisibleRowsAndClearUpdated,
        cellStatus,
        setCellStatus,
      }}
      {...props}
    />
  );
}

const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context)
    throw new Error("useReportContext must be used within a ReportProvider");
  return context;
};

export { ReportProvider, useReportContext };
